import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import useOutsideClick from "hooks/click-outside-hook";

interface NotificationData {
  title: string;
  description: string;
  createdAt?: string;
  type?: string;
  sender?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  content: NotificationData | null;
}

const NotificationModal: React.FC<Props> = ({ isOpen, onClose, content }) => {
  const modalRef = useOutsideClick(onClose);

  if (!isOpen || !content) return null;

  const formattedDate = content.createdAt
    ? new Date(content.createdAt).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const senderName =
    content.sender &&
    `${content.sender.firstName || ""} ${content.sender.lastName || ""}`.trim();

  return (
    <div
      className={`fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        ref={modalRef}
        className={`relative bg-white p-6 rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto sm:w-[520px] w-full mx-4 transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
        >
          <AiOutlineClose size={22} />
        </button>

        {/* Header */}
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-goldenOrangeBg mb-1">
            {content.type || "Notification"}
          </p>
          <h2 className="text-2xl font-bold text-primaryText leading-snug">
            {content.title}
          </h2>

          {(senderName || formattedDate) && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {senderName && (
                <span className="inline-flex items-center rounded-full bg-coolLightGrayBg px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-goldenOrangeBg mr-2" />
                  From: {senderName}
                </span>
              )}
              {formattedDate && (
                <span className="inline-flex items-center rounded-full bg-coolLightGrayBg px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-darkTealBlue mr-2" />
                  {formattedDate}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-borderLightGray to-transparent mb-4" />

        {/* Body */}
        <div className="text-sm text-gray-700 leading-relaxed max-h-72 overflow-y-auto whitespace-pre-line">
          {content.description}
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
